import { zodResolver } from '@hookform/resolvers/zod';
import {
  COMMENT_BODY_MAX,
  COMMENT_BODY_MIN,
  createCommentSchema,
  type CreateCommentInput,
} from '@zoxo/shared';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { describeSubmitError } from '../../lib/describe-submit-error';
import { GatePrompt, useActionGate } from '../auth/action-gate';
import type { CommentTarget } from './comment-target';
import { useCreateComment } from './use-comment-mutations';

/**
 * Comment compose form (AC-3). RHF + the shared createCommentSchema. Gated by
 * login + onboarding: an anonymous / not-onboarded user sees the login/onboarding
 * prompt in place of the textarea (AC-0.2/0.3). On success the field resets and
 * the list refetches (invalidate).
 *
 * Reused inline as a reply form: pass `parentId` (the top-level comment id — the
 * server flattens depth >2 onto that parent) and optionally `defaultBody` for a
 * `@nickname ` prefill (YouTube-style). `onSuccess`/`onCancel` let the caller
 * close the inline form while keeping the thread expanded.
 */
export function CommentForm({
  target,
  parentId,
  defaultBody = '',
  autoFocus = false,
  onSuccess,
  onCancel,
}: {
  target: CommentTarget;
  parentId?: string;
  defaultBody?: string;
  autoFocus?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
}) {
  const gate = useActionGate();
  const create = useCreateComment(target);
  const isReply = parentId !== undefined;
  const fieldId = isReply ? `reply-body-${parentId}` : 'comment-body';
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateCommentInput>({
    resolver: zodResolver(createCommentSchema),
    defaultValues: { body: defaultBody },
  });

  if (gate !== 'ready') {
    return (
      <GatePrompt
        status={gate}
        message={isReply ? '답글을 쓰려면 로그인이 필요해요.' : '댓글을 쓰려면 로그인이 필요해요.'}
      />
    );
  }

  const onSubmit = handleSubmit((values) =>
    create.mutate(
      // Only attach parentId for replies; top-level submits stay body-only.
      isReply ? { ...values, parentId } : values,
      {
        onSuccess: () => {
          reset({ body: '' });
          onSuccess?.();
        },
      },
    ),
  );

  return (
    <form
      className={isReply ? 'mb-3 flex flex-col gap-2' : 'mb-4 flex flex-col gap-2'}
      onSubmit={(e) => void onSubmit(e)}
      noValidate
    >
      <div className="flex flex-col gap-1">
        <label htmlFor={fieldId} className="sr-only">
          {isReply ? '답글' : '댓글'}
        </label>
        <textarea
          id={fieldId}
          rows={3}
          autoFocus={autoFocus}
          placeholder={isReply ? '답글 쓰기.' : '의견 쓰기.'}
          maxLength={COMMENT_BODY_MAX}
          aria-invalid={errors.body ? 'true' : undefined}
          className="w-full resize-y rounded-lg border border-border-strong bg-surface px-3 py-2 text-sm leading-relaxed text-text outline-none transition-colors placeholder:text-text-subtle focus:border-primary focus:ring-2 focus:ring-primary/20 aria-invalid:border-danger"
          {...register('body')}
        />
        {errors.body && (
          <p className="text-xs text-danger" role="alert">
            {isReply ? '답글' : '댓글'}을 {COMMENT_BODY_MIN}~{COMMENT_BODY_MAX}자로 입력하세요.
          </p>
        )}
        {create.isError && (
          <p className="text-xs text-danger" role="alert">
            {describeSubmitError(create.error)}
          </p>
        )}
      </div>
      {/* Secondary: on the detail screen the single primary is the vote CTA, so
          comment/reply submits stay bordered secondary to avoid competing primaries. */}
      <div className="flex flex-wrap items-center gap-2">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            취소
          </Button>
        )}
        <Button type="submit" variant="secondary" disabled={isSubmitting || create.isPending}>
          {create.isPending
            ? isReply
              ? '답글 등록 중…'
              : '등록 중…'
            : isReply
              ? '답글 등록'
              : '댓글 등록'}
        </Button>
      </div>
    </form>
  );
}
